import {
    type AppStore,
    AppStoreBadge,
    type AppStoreBadgeSize,
    type AppStoreBadgeStyle,
} from '@trustoo/components/AppStoreBadge/v2'
import React from 'react'
import { CodeSnippet, InlineCode, Section } from './shared'

// ---------- App store badge (v2) showcase ----------
// A brand-artwork component: the trademarked store lockups are inlined verbatim
// as SVG. Scoped to the two stores Trustoo distributes through (App Store +
// Google Play). Generated from Figma's "Mobile app store badge" set.

const STORES: AppStore[] = ['app-store', 'google-play']
const STYLES: AppStoreBadgeStyle[] = ['brand', 'outline']
const SIZES: AppStoreBadgeSize[] = ['md', 'lg']

const storeLabel = (store: AppStore): string =>
    store === 'app-store' ? 'App Store' : 'Google Play'

const APP_STORE_BADGE_USAGE = `import { AppStoreBadge } from '@trustoo/components/AppStoreBadge/v2'

// Non-interactive mark
<AppStoreBadge store="app-store" />

// As a link (renders an <a>, no extra chrome)
<AppStoreBadge store="google-play" href="https://play.google.com/store/apps/details?id=…" />

// Style: brand (black field) · outline (white field). Size: md (40px) · lg (44px)
<AppStoreBadge store="app-store" badgeStyle="outline" size="lg" />`

export const AppStoreBadgeShowcase: React.FC = () => (
    <Section
        title="App store badge (v2 component)"
        description={
            <>
                Generated from Figma's <span className="font-semibold">Mobile app store badge</span>{' '}
                set (ComponentSet <InlineCode>5:5247</InlineCode>). A{' '}
                <span className="font-semibold">brand-artwork</span> component: the trademarked
                store lockups are inlined verbatim as SVG. Scoped to the two stores Trustoo
                distributes through. Import v2 explicitly via{' '}
                <InlineCode>@trustoo/components/AppStoreBadge/v2</InlineCode> (net-new component —
                there is no v1 barrel).
            </>
        }>
        <div className="space-y-spacing-3xl">
            {STYLES.map((badgeStyle) => (
                <div
                    key={badgeStyle}
                    className="rounded-radius-xs border border-border-secondary bg-base-white p-spacing-xl">
                    <div className="mb-spacing-lg flex items-baseline gap-spacing-md">
                        <span className="text-sm font-semibold text-text-primary">
                            {badgeStyle.charAt(0).toUpperCase() + badgeStyle.slice(1)}
                        </span>
                        <InlineCode>badgeStyle=&quot;{badgeStyle}&quot;</InlineCode>
                    </div>
                    <div className="flex flex-wrap items-end gap-x-spacing-3xl gap-y-spacing-xl">
                        {SIZES.map((size) => (
                            <div key={size} className="space-y-spacing-md">
                                <div className="text-xs font-semibold uppercase text-text-quaternary">
                                    {size}
                                </div>
                                <div className="flex flex-wrap items-center gap-spacing-lg">
                                    {STORES.map((store) => (
                                        <AppStoreBadge
                                            key={store}
                                            store={store}
                                            badgeStyle={badgeStyle}
                                            size={size}
                                        />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ))}

            <div>
                <h3 className="mb-spacing-lg text-sm font-semibold text-text-primary">
                    How to use
                </h3>
                <CodeSnippet code={APP_STORE_BADGE_USAGE} />
                <p className="mt-spacing-lg text-xs text-text-tertiary">
                    Props: <InlineCode>store</InlineCode> (
                    {STORES.map((s) => storeLabel(s)).join(' · ')}),{' '}
                    <InlineCode>badgeStyle</InlineCode> (brand · outline),{' '}
                    <InlineCode>size</InlineCode> (md · lg), and an optional{' '}
                    <InlineCode>href</InlineCode> that turns the badge into a link. The badge is a
                    non-interactive <InlineCode>role=&quot;img&quot;</InlineCode> mark by default
                    and always carries an accessible store name.
                </p>
            </div>
        </div>
    </Section>
)
